<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAccount } from 'dashboard/composables/useAccount';
import {
  getConversationDisplayIdentifier,
  getConversationDisplayName,
  getConversationTicketTitle,
} from 'dashboard/helper/conversationHelper';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  isDragging: {
    type: Boolean,
    default: false,
  },
  isPersisting: {
    type: Boolean,
    default: false,
  },
  canAssignToMe: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['assign-to-me', 'drag-start', 'drag-end']);

const { t } = useI18n();
const { accountId } = useAccount();

const contactName = computed(() => {
  return (
    props.conversation.meta?.sender?.name || t('KANBAN.CARD.UNKNOWN_CONTACT')
  );
});

const ticketTitle = computed(() => getConversationTicketTitle(props.conversation));

const conversationTitle = computed(() =>
  getConversationDisplayName(props.conversation, contactName.value)
);

const conversationIdentifier = computed(() =>
  getConversationDisplayIdentifier(props.conversation)
);

const assigneeName = computed(() => {
  return (
    props.conversation.meta?.assignee?.name || t('KANBAN.CARD.UNASSIGNED')
  );
});

const lastMessagePreview = computed(() => {
  return (
    props.conversation.last_non_activity_message?.content ||
    props.conversation.messages?.[0]?.content ||
    t('KANBAN.CARD.NO_MESSAGES')
  );
});

const conversationLink = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: props.conversation.id,
    })
  );
});

const statusKey = computed(() => {
  return `KANBAN.CARD.STATUS_${props.conversation.status?.toUpperCase()}`;
});

const onDragStart = event => {
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', String(props.conversation.id));
  emit('drag-start', props.conversation);
};
</script>

<template>
  <article
    draggable="true"
    class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-alpha-3 p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    :class="{
      'cursor-grabbing opacity-70': isDragging,
      'pointer-events-none opacity-60': isPersisting,
      'cursor-grab': !isDragging && !isPersisting,
    }"
    @dragstart="onDragStart"
    @dragend="$emit('drag-end')"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <p class="truncate text-sm font-semibold text-n-slate-12">
          {{ conversationTitle }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ conversationIdentifier }}
          <span v-if="ticketTitle">
            · {{ contactName }}
          </span>
          <span v-if="conversation.meta?.team?.name">
            · {{ conversation.meta.team.name }}
          </span>
        </p>
      </div>
      <span
        class="rounded-full bg-n-alpha-black2 px-2 py-1 text-[11px] font-medium text-n-slate-11"
      >
        {{ t(statusKey) }}
      </span>
    </div>

    <p class="line-clamp-3 text-sm text-n-slate-11">
      {{ lastMessagePreview }}
    </p>

    <div class="flex flex-col gap-1 text-xs text-n-slate-11">
      <p>
        {{ t('KANBAN.CARD.ASSIGNEE') }}: {{ assigneeName }}
      </p>
      <p v-if="conversation.meta?.sender?.email">
        {{ conversation.meta.sender.email }}
      </p>
      <p v-if="conversation.meta?.sender?.phone_number">
        {{ conversation.meta.sender.phone_number }}
      </p>
    </div>

    <div class="flex items-center gap-2 pt-1">
      <router-link
        :to="conversationLink"
        class="inline-flex items-center rounded-xl border border-n-strong px-3 py-2 text-xs font-medium text-n-slate-12 transition hover:bg-n-alpha-black2"
      >
        {{ t('KANBAN.CARD.OPEN_CONVERSATION') }}
      </router-link>
      <button
        v-if="canAssignToMe"
        type="button"
        class="inline-flex items-center rounded-xl bg-n-slate-12 px-3 py-2 text-xs font-medium text-white transition hover:opacity-90"
        @click.stop="$emit('assign-to-me', conversation)"
      >
        {{ t('KANBAN.CARD.ASSIGN_TO_ME') }}
      </button>
    </div>
  </article>
</template>
